import ReactDOM from 'react-dom/client';
import { lazy, StrictMode, Suspense } from 'react';
import GlobalLoading from './GlobalLoading';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import AppErrorBoundary from './AppErrorBoundary';
import { version } from '../version';
import RegisterSW from './RegisterSW';
import { AccountRole } from '@football-kids/shared/enums';
import { noiseErrorsArray } from '@football-kids/shared/utils/sentry';
import { TenantContext } from './context/TenantContext';
import { getEnv } from './utils/hooks';

import { MedusaContextProvider } from '@football-kids/inventory-client';

// if (!import.meta.env.DEV) {
    const sentryDsn = getEnv('VITE_SENTRY_DSN');
    console.log('sentryDsn', sentryDsn);
    if (sentryDsn) {
        import('@sentry/react').then((Sentry) => {
            (window as any).Sentry = Sentry;

            Sentry.init({
                dsn: sentryDsn,
                release: version,
                environment: import.meta.env.VITE_SENTRY_ENV,
                tracesSampleRate: Number(import.meta.env.VITE_SENTRY_SAMPLE_RATE),
                integrations: [
                    Sentry.browserTracingIntegration({}),
                    Sentry.extraErrorDataIntegration({
                        captureErrorCause: true,
                    }),
                ],
                ...(import.meta.env.VITE_PWA_API_REGEX && {
                    tracePropagationTargets: [
                        new RegExp(import.meta.env.VITE_PWA_API_REGEX)
                    ]
                }),
                ignoreErrors: noiseErrorsArray,
                beforeSend(event, hint) {
                    // Check if it is an exception, and if so, show the report dialog
                    if (
                        event.exception &&
                        event.event_id &&
                        [
                            AccountRole.ADMIN,
                            AccountRole.MANAGER,
                            AccountRole.COACH,
                        ].includes(event.contexts?.account?.role as any)
                    ) {
                        Sentry.showReportDialog({
                            eventId: event.event_id,

                            title: `Wysyłanie raportu`,
                            subtitle: `Jeśli masz życzenie nam pomóc, możesz opisać sytuację która doprowadziła do tego błędu.`,
                            subtitle2: `Dziękujemy za twoją pomoc`,
                            labelName: 'Imię (Opcjonalne)',
                            labelEmail: `Email (Opcjonalne)`,
                            labelComment: 'Co się stało',
                            labelClose: 'Zamknij',
                            labelSubmit: 'Wyślij raport',
                            onClose() {
                                window.location.reload();
                            },
                        });
                    }
                    return event;
                },
            });
        });
    }
//}

console.log(`Wersja aplikacji: ${version}`);

const startTime = performance.now();
console.log('startTime', startTime);

const App = lazy(() => {
    console.log('appLoadingStartTime', performance.now());

    return import('./App')
        .catch((e, ...args) => {
            console.log('appLoadingCatch', performance.now());

            console.log(e);
            console.log(args);

            if ((window as any)?.Sentry) {
                (window as any)?.Sentry.captureException(
                    new Error('app loading catch')
                );
            }

            return { default: GlobalLoading };
        })
        .finally(() => {
            if (startTime) {
                console.log('appLoadingEndTime', performance.now());
                const duration = performance.now() - startTime;

                if (window.Sentry && duration > 60 * 30) {
                    window.Sentry?.setContext?.('connection', {
                        loadingDuration: duration,
                        downlink: navigator.connection?.downlink,
                        effectiveType: navigator.connection?.effectiveType,
                    });
                    window?.Sentry.captureException(
                        new Error('app loading too long')
                    );

                    if (
                        navigator?.connection?.downlink &&
                        navigator?.connection?.downlink > 3
                    ) {
                        window.location.reload(true);
                    }
                }
            }
        });
});

const router = createBrowserRouter([
    {
        path: '*',
        element: <App />,
        errorElement: <div>
            Wystąpił błąd. Spróbuj odświeżyć stronę i skontaktuj
            się z administratorem
        </div>
    },
]);

const main = async ({ theme, globalStyles }: any) => {
    const root = ReactDOM.createRoot(document.getElementById('root')!);
    root.render(<GlobalLoading />);

    try {
        const response = await fetch(`${getEnv('VITE_SERVER_URL')}/api/config`);

        if (!response.ok) {
            throw new Error(`Failed to load config: ${response.status}`);
        }

        const config = await response.json();
        const refreshCounter = Number(localStorage.getItem('_refreshCounter') || "0")

        if (config.version !== version && refreshCounter < 2) {
            window.location.reload()
            localStorage.setItem("_refreshCounter", `${refreshCounter + 1}`);
            return
        }

        localStorage.setItem("_refreshCounter", `${0}`);

        root.render(
            <StrictMode>
                <RegisterSW />
                <Suspense fallback={<GlobalLoading />}>
                    <AppErrorBoundary
                        fallback={
                            <div>
                                Wystąpił błąd. Spróbuj odświeżyć stronę i skontaktuj
                                się z administratorem
                            </div>
                        }
                    >
                        <TenantContext.Provider value={{ theme, globalStyles, config }}>
                            <MedusaContextProvider>
                                <RouterProvider router={router} />
                            </MedusaContextProvider>
                        </TenantContext.Provider>
                    </AppErrorBoundary>
                </Suspense>
            </StrictMode>
        );

    } catch (error) {
        console.error(error);
        root.render(
            <div>
                Nie udało się pobrać konfiguracji. Spróbuj odświeżyć stronę.
            </div>
        );
        return;
    }
};

export default main;
